// CondominiumMultiSelector.tsx

import { useEffect, useState } from "react";
import { useToast } from "../ui/use-toast";
import { useDebounce } from "@/hooks/useDebounce";

interface Condominium {
  id: number;
  attributes: {
    Name: string;
    Address?: string;
  };
}

interface CondominiumMultiSelectorProps {
  value: string[];
  onChange: (values: string[]) => void;
  className?: string;
  maxHeight?: string;
}

export function CondominiumMultiSelector({
  value,
  onChange,
  className = "",
  maxHeight = "max-h-60",
}: CondominiumMultiSelectorProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [condominiums, setCondominiums] = useState<Condominium[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const route = import.meta.env.VITE_URL_ENDPOINT;

  // Buscar edifícios quando o componente monta ou quando o termo de busca muda
  useEffect(() => {
    const fetchCondominiums = async () => {
      try {
        setLoading(true);
        let url = `${route}/api/condominiums?pagination[pageSize]=100`;

        if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) {
          url += `&filters[Name][$containsi]=${debouncedSearchTerm}`;
        }

        const response = await fetch(url);
        const result = await response.json();
        setCondominiums(result.data || []);
      } catch (error) {
        console.error("Erro ao buscar edifícios:", error);
        toast({
          title: "Erro",
          description: "Erro ao buscar edifícios.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchCondominiums();
    }
  }, [debouncedSearchTerm, isOpen, route, toast]);

  const handleToggleCondominium = (condominiumName: string) => {
    const newValue = value.includes(condominiumName)
      ? value.filter((v) => v !== condominiumName)
      : [...value, condominiumName];
    onChange(newValue);
  };

  const handleRemoveCondominium = (condominiumName: string) => {
    onChange(value.filter((v) => v !== condominiumName));
  };

  const getDisplayText = () => {
    if (value.length === 0) return "Selecione os edifícios";
    if (value.length === 1) return value[0];
    return `${value.length} edifícios selecionados`;
  };

  return (
    <div className={`relative ${className}`}>
      <label className="mb-1 block text-base font-semibold text-gray-800">
        Edifícios
      </label>

      {/* Selected condominiums display */}
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {value.map((condominiumName) => (
            <span
              key={condominiumName}
              className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800"
            >
              {condominiumName}
              <button
                type="button"
                onClick={() => handleRemoveCondominium(condominiumName)}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span
          className={value.length === 0 ? "text-gray-500" : "text-gray-900"}
        >
          {getDisplayText()}
        </span>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"} text-gray-400`}
        ></i>
      </button>

      {/* Dropdown content */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-300 bg-white shadow-lg">
          {/* Search input */}
          <div className="border-b border-gray-200 p-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar edifício..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded border border-gray-300 py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 transform text-sm text-gray-400"></i>
            </div>
          </div>

          {/* Condominiums list */}
          <div className={`${maxHeight} overflow-y-auto`}>
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Carregando...
              </div>
            ) : condominiums.length > 0 ? (
              condominiums.map((condominium) => {
                const isSelected = value.includes(condominium.attributes.Name);

                return (
                  <div
                    key={condominium.id}
                    onClick={() =>
                      handleToggleCondominium(condominium.attributes.Name)
                    }
                    className="flex cursor-pointer items-center px-3 py-2 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Controlled by onClick
                      className="mr-3 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {condominium.attributes.Name}
                      </div>
                      {condominium.attributes.Address && (
                        <div className="text-xs text-gray-500">
                          {condominium.attributes.Address}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-gray-500">
                {searchTerm
                  ? "Nenhum edifício encontrado"
                  : "Nenhum edifício disponível"}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-200 p-2">
            <span className="text-xs text-gray-500">
              {value.length} selecionados
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
